'use server'

import { apiRequest, ApiResponse } from '../request'

// ─── Interfaces ─────────────────────────────────────────────────

export interface EmailAccount {
    id: number
    email: string
    name?: string
    picture?: string
    provider: 'GMAIL'
    status: 'ACTIVE' | 'DISCONNECTED' | 'ERROR'
    lastSyncAt?: string
    lastError?: string
    createdAt: string
    connectedBy?: { id: number; name: string }
}

export interface EmailMessageSummary {
    id: number
    externalId: string
    emailAccountId: number
    threadId?: string
    subject?: string
    fromAddress?: string
    fromName?: string
    snippet?: string
    isRead: boolean
    isStarred: boolean
    hasAttachments: boolean
    labels?: string
    receivedAt?: string
    emailAccount?: {
        email: string
        name?: string
        picture?: string
    }
}

export interface EmailAttachmentInfo {
    id: number
    attachmentId: string
    filename?: string
    mimeType?: string
    size?: number
}

export interface EmailMessageDetail extends EmailMessageSummary {
    toAddresses?: string
    ccAddresses?: string
    bccAddresses?: string
    messageIdHeader?: string
    bodyHtml?: string
    bodyText?: string
    attachments: EmailAttachmentInfo[]
    emailAccount?: {
        id: number
        email: string
        name?: string
        picture?: string
    }
}

export interface EmailDraftSummary {
    id: number
    emailAccountId: number
    subject?: string
    toAddresses?: string
    updatedAt: string
    createdAt: string
}

export interface EmailDraftDetail extends EmailDraftSummary {
    externalDraftId?: string
    threadId?: string
    inReplyTo?: string
    references?: string
    ccAddresses?: string
    bccAddresses?: string
    bodyHtml?: string
}

export interface EmailTemplate {
    id: number
    name: string
    subject?: string
    bodyHtml: string
    createdAt: string
    updatedAt: string
}

export interface EmailLabel {
    id: string
    name: string
    type: 'system' | 'user'
}

export type EmailFolder =
    | 'INBOX'
    | 'STARRED'
    | 'SENT'
    | 'DRAFT'
    | 'TRASH'
    | 'ALL'

// ─── Contas ─────────────────────────────────────────────────────

export async function getEmailAccounts(): Promise<
    ApiResponse<EmailAccount[]>
> {
    return apiRequest<EmailAccount[]>('inbox/accounts', {})
}

export async function getOAuthUrl(): Promise<
    ApiResponse<{ url: string }>
> {
    return apiRequest<{ url: string }>('inbox/oauth/url', {})
}

export async function disconnectEmailAccount(
    accountId: number,
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`inbox/accounts/${accountId}`, {}, 'delete')
}

// ─── Mensagens ──────────────────────────────────────────────────

export async function getEmailMessages(
    params?: {
        page?: number
        limit?: number
        isRead?: string
        accountId?: number
        query?: string
        folder?: string
    },
): Promise<ApiResponse<EmailMessageSummary[]>> {
    return apiRequest<EmailMessageSummary[]>('inbox/messages', { params })
}

export async function getEmailMessage(
    id: number,
): Promise<ApiResponse<EmailMessageDetail>> {
    return apiRequest<EmailMessageDetail>(`inbox/messages/${id}`, {})
}

export async function toggleEmailReadStatus(
    id: number,
    isRead: boolean,
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `inbox/messages/${id}/read`,
        { data: { isRead } },
        'patch',
    )
}

export function getAttachmentDownloadUrl(
    messageId: number,
    attachmentId: number,
): string {
    const baseURL = import.meta.env.VITE_API_BASE_URL
    return `${baseURL}/inbox/messages/${messageId}/attachments/${attachmentId}/download`
}

// ─── Enviar / Responder / Encaminhar ────────────────────────────

export async function sendEmail(
    formData: FormData,
): Promise<ApiResponse<{ success: boolean; messageId: string }>> {
    return apiRequest<{ success: boolean; messageId: string }>(
        'inbox/messages/send',
        { data: formData, formData: true },
        'post',
    )
}

export async function replyToEmail(
    formData: FormData,
): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(
        'inbox/messages/reply',
        { data: formData, formData: true },
        'post',
    )
}

export async function forwardEmail(
    formData: FormData,
): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(
        'inbox/messages/forward',
        { data: formData, formData: true },
        'post',
    )
}

// ─── Thread ─────────────────────────────────────────────────────

export async function getEmailThread(
    threadId: string,
    accountId: number,
): Promise<ApiResponse<EmailMessageDetail[]>> {
    return apiRequest<EmailMessageDetail[]>(
        `inbox/threads/${threadId}`,
        { params: { accountId } },
    )
}

// ─── Estrelar / Arquivar / Lixeira ──────────────────────────────

export async function toggleEmailStar(
    id: number,
    isStarred: boolean,
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `inbox/messages/${id}/star`,
        { data: { isStarred } },
        'patch',
    )
}

export async function archiveEmail(
    id: number,
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`inbox/messages/${id}/archive`, {}, 'post')
}

export async function trashEmail(
    id: number,
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`inbox/messages/${id}/trash`, {}, 'post')
}

export async function untrashEmail(
    id: number,
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`inbox/messages/${id}/untrash`, {}, 'post')
}

// ─── Rascunhos ──────────────────────────────────────────────────

export async function saveDraft(
    data: Record<string, unknown>,
): Promise<ApiResponse<EmailDraftDetail>> {
    return apiRequest<EmailDraftDetail>('inbox/drafts', { data }, 'post')
}

export async function getDrafts(
    accountId?: number,
): Promise<ApiResponse<EmailDraftSummary[]>> {
    return apiRequest<EmailDraftSummary[]>(
        'inbox/drafts',
        { params: accountId ? { accountId } : {} },
    )
}

export async function getDraft(
    id: number,
): Promise<ApiResponse<EmailDraftDetail>> {
    return apiRequest<EmailDraftDetail>(`inbox/drafts/${id}`, {})
}

export async function deleteDraft(
    id: number,
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`inbox/drafts/${id}`, {}, 'delete')
}

export async function sendDraftEmail(
    id: number,
    formData?: FormData,
): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(
        `inbox/drafts/${id}/send`,
        { data: formData || {}, formData: !!formData },
        'post',
    )
}

// ─── Labels ─────────────────────────────────────────────────────

export async function getAccountLabels(
    accountId: number,
): Promise<ApiResponse<EmailLabel[]>> {
    return apiRequest<EmailLabel[]>(
        `inbox/accounts/${accountId}/labels`,
        {},
    )
}

// ─── Assinatura ─────────────────────────────────────────────────

export async function getAccountSignature(
    accountId: number,
): Promise<ApiResponse<{ signatureHtml: string | null }>> {
    return apiRequest<{ signatureHtml: string | null }>(
        `inbox/accounts/${accountId}/signature`,
        {},
    )
}

export async function updateAccountSignature(
    accountId: number,
    signatureHtml: string,
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `inbox/accounts/${accountId}/signature`,
        { data: { signatureHtml } },
        'put',
    )
}

// ─── Templates ──────────────────────────────────────────────────

export async function getEmailTemplates(): Promise<
    ApiResponse<EmailTemplate[]>
> {
    return apiRequest<EmailTemplate[]>('inbox/templates', {})
}

export async function createEmailTemplate(
    data: { name: string; subject?: string; bodyHtml: string },
): Promise<ApiResponse<EmailTemplate>> {
    return apiRequest<EmailTemplate>('inbox/templates', { data }, 'post')
}

export async function getEmailTemplate(
    id: number,
): Promise<ApiResponse<EmailTemplate>> {
    return apiRequest<EmailTemplate>(`inbox/templates/${id}`, {})
}

export async function updateEmailTemplate(
    id: number,
    data: { name?: string; subject?: string; bodyHtml?: string },
): Promise<ApiResponse<EmailTemplate>> {
    return apiRequest<EmailTemplate>(
        `inbox/templates/${id}`,
        { data },
        'put',
    )
}

export async function deleteEmailTemplate(
    id: number,
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`inbox/templates/${id}`, {}, 'delete')
}

// ─── Contatos ───────────────────────────────────────────────────

export interface EmailContact {
    email: string
    name?: string | null
    source: 'contact' | 'client' | 'partner'
}

export async function searchEmailContacts(
    query?: string,
): Promise<ApiResponse<EmailContact[]>> {
    return apiRequest<EmailContact[]>('inbox/contacts', {
        params: query ? { q: query } : {},
    })
}
